import React from 'react';
import { Card, Typography, Button, Space, Spin, Progress, Tooltip, Switch, InputNumber, Modal, Select } from 'antd';
import { AppstoreOutlined, TableOutlined, OrderedListOutlined, FilterOutlined, LineChartOutlined, FullscreenOutlined, CalendarOutlined } from '@ant-design/icons';
import type { ForecastHourly, HourlyForecast, HourlyForecastParameter } from '../../api/forecastService';
import type { Session } from '../../api/eventsService';
import type { ForecastField } from '../../api/forecastFieldsService';
import {
  getParameterValue,
  getWeatherCodeForHourly,
} from '../../utils/weatherUtils';
import { WindDirectionIcon } from './WindDirectionIcon';
import { useTheme } from '../../contexts/useTheme';
import { useEventSelection } from '../../contexts/useEventSelection';
import { useForecastFields } from '../../hooks/useForecastFields';
import { 
  getFieldRenderConfig, 
  isFieldCombined, 
  parseWindValue, 
  parsePercentageValue,
  getFieldIcon,
  type FieldRenderConfig 
} from './fieldRenderers';
import { EditableTextModal } from './EditableTextModal';
import { EditableParameterPopover } from './EditableParameterPopover';
import { WeatherIconPicker } from './WeatherIconPicker';
import { ForecastMarkdown } from './ForecastMarkdown';
import { CollapsibleText } from './CollapsibleText';
import { FieldReorderModal } from './FieldReorderModal';
import { ForecastChartView } from './ForecastChartView';

const { Title, Text } = Typography;

/** True if local clock time in `timeZone` is exactly :00:00. */
function isOnTheHourInTimezone(date: Date, timeZone: string): boolean {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hour12: false,
  }).formatToParts(date);
  const minute = parseInt(parts.find(p => p.type === 'minute')?.value ?? '0', 10);
  const second = parseInt(parts.find(p => p.type === 'second')?.value ?? '0', 10);
  return minute === 0 && second === 0;
}

function formatTimeHHmmInTimezone(date: Date, timeZone: string): string {
  return date.toLocaleString('en-US', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

/**
 * For an hourly slot starting at `hourStart`, returns a time fragment to disambiguate
 * session boundaries that are not on the round hour (e.g. FP1 10:30 vs the 10:00 column).
 */
function getSessionTimeQualifierForHour(
  hourStart: Date,
  session: Session,
  timeZone: string,
): { time: string } | null {
  const hourStartMs = hourStart.getTime();
  const hourEndMs = hourStartMs + 60 * 60 * 1000;
  const sessionStartMs = new Date(session.start_time).getTime();
  const sessionEndMs = new Date(session.end_time).getTime();
  const startDate = new Date(session.start_time);
  const endDate = new Date(session.end_time);

  const fmt = (d: Date) => formatTimeHHmmInTimezone(d, timeZone);
  const onHour = (d: Date) => isOnTheHourInTimezone(d, timeZone);

  const startInHour = sessionStartMs >= hourStartMs && sessionStartMs < hourEndMs;
  const endInHour = sessionEndMs > hourStartMs && sessionEndMs <= hourEndMs;

  if (startInHour && endInHour) {
    const startRound = onHour(startDate);
    const endRound = onHour(endDate);
    if (!startRound && !endRound) return { time: `${fmt(startDate)}–${fmt(endDate)}` };
    if (!startRound) return { time: fmt(startDate) };
    if (!endRound) return { time: fmt(endDate) };
    return null;
  }
  if (startInHour && !onHour(startDate)) return { time: fmt(startDate) };
  if (endInHour && !onHour(endDate)) return { time: fmt(endDate) };
  return null;
}

function formatCardSessionParts(overlapping: Session[], hourStart: Date, timeZone: string): string {
  return overlapping
    .map(s => {
      const q = getSessionTimeQualifierForHour(hourStart, s, timeZone);
      return q ? `${s.session_name} ${q.time}` : s.session_name;
    })
    .join(', ');
}

function formatTableSessionParts(overlapping: Session[], hourStart: Date, timeZone: string): string {
  return overlapping
    .map(s => {
      const q = getSessionTimeQualifierForHour(hourStart, s, timeZone);
      return q ? `${s.session_name} (${q.time})` : s.session_name;
    })
    .join(', ');
}

function formatDateKeyForHeader(dateKey: string): string {
  const [year, month, day] = dateKey.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day, 12))
    .toLocaleDateString('en-US', {
      timeZone: 'UTC',
      weekday: 'short',
      month: 'short',
      day: '2-digit',
    })
    .toUpperCase();
}

// Helper component to handle weather icon with fallback
const WeatherIconWithFallback: React.FC<{
  iconPath: string;
  size: number;
}> = ({ iconPath, size }) => {
  const [hasError, setHasError] = React.useState(false);

  // If icon failed to load, show emoji or fallback
  if (hasError) {
    return <span style={{ fontSize: size * 0.75 }}>❓</span>;
  }

  // If it's an icon path, try to load it
  if (iconPath.startsWith('/')) {
    return (
      <img 
        src={iconPath}
        alt="Weather icon"
        style={{ width: size, height: size }}
        onError={() => setHasError(true)}
      />
    );
  }

  // Otherwise it's already an emoji
  return <span style={{ fontSize: size * 0.75 }}>{iconPath}</span>;
};

interface HourlyForecastViewProps {
  forecasts: ForecastHourly[];
  selectedDate: Date | null;
  viewMode?: 'cards' | 'table' | 'chart';
  onViewModeChange?: (mode: 'cards' | 'table' | 'chart') => void;
  // Optional callbacks for root users to edit text fields and parameters
  isEditable?: boolean;
  onSavePreviousNight?: (valueDate: string, newText: string, dateKey?: string) => Promise<void>;
  onSaveWeatherSummary?: (valueDate: string, newText: string, dateKey?: string) => Promise<void>;
  onSaveParameter?: (valueDate: string, forecastDatetime: string, paramName: string, newValue: string) => Promise<void>;
  // Date filter (opt-in, from event range)
  dateFilterEnabled?: boolean;
  onDateFilterEnabledChange?: (enabled: boolean) => void;
  eventDateRange?: string[];
  selectedFilterDates?: string[];
  onFilterDatesChange?: (dates: string[]) => void;
  statusExtra?: React.ReactNode;
}

// Dynamic table time slot - stores raw parameters for flexible rendering
interface DynamicTableTimeSlot {
  datetime: number;
  datetimeIso: string;
  valueDate: string;
  timeStr: string;
  dateStr: string;
  parameters: HourlyForecastParameter[];
}

// Day-level forecast data (from forecastsByDay)
interface ForecastDayData {
  dateKey: string;
  date: Date;
  forecastIssueTime: Date;
  valueDate: string;
  previousNight: string | null;
  weatherSummary: string | null;
  alertLevel: string | null;
  forecasts: HourlyForecast[];
}

export const HourlyForecastView: React.FC<HourlyForecastViewProps> = ({ 
  forecasts, 
  selectedDate,
  viewMode: controlledViewMode,
  onViewModeChange,
  isEditable = false,
  onSavePreviousNight,
  onSaveWeatherSummary,
  onSaveParameter,
  dateFilterEnabled = false,
  onDateFilterEnabledChange,
  eventDateRange = [],
  selectedFilterDates = [],
  onFilterDatesChange,
  statusExtra,
}) => {
  const { theme } = useTheme();
  const { selectedEvent } = useEventSelection();
  const { hourlyCardsFields, hourlyTableFields, loading: fieldsLoading, createDefaultField, fieldsByDbName } = useForecastFields();
  const isDark = theme === 'dark';
  const timezone = selectedEvent?.circuit?.time_zone || 'UTC';
  
  // Support both controlled and uncontrolled mode
  const [internalViewMode, setInternalViewMode] = React.useState<'cards' | 'table' | 'chart'>('cards');
  const viewMode = controlledViewMode ?? internalViewMode;
  const setViewMode = onViewModeChange ?? setInternalViewMode;

  // Field reorder modal state
  const [isReorderModalOpen, setIsReorderModalOpen] = React.useState(false);
  const [savingOrder, setSavingOrder] = React.useState(false);
  const [orderVersion, setOrderVersion] = React.useState(0);

  // Session filter state
  const [filterBySession, setFilterBySession] = React.useState(false);
  const [hoursBeforeSession, setHoursBeforeSession] = React.useState(2);

  // Compact cards mode - hides Previous Night and Weather Conditions sections
  const [compactCards, setCompactCards] = React.useState(false);

  // Modal state for viewing full day details
  const [openModalDayKey, setOpenModalDayKey] = React.useState<string | null>(null);

  // When Sessions Only is toggled on, automatically enable compact cards
  const handleSessionFilterChange = React.useCallback((checked: boolean) => {
    setFilterBySession(checked);
    if (checked) {
      setCompactCards(true);
    }
  }, []);

  // Get sessions from selected event (memoized to prevent unnecessary re-renders)
  const sessions = React.useMemo(() => selectedEvent?.sessions || [], [selectedEvent?.sessions]);

  // Check if a given datetime falls within any session window (including hours before)
  const isWithinSessionWindow = React.useCallback((datetime: Date): boolean => {
    if (!filterBySession || sessions.length === 0) return true;

    const hourStart = datetime.getTime();
    const oneHourMs = 60 * 60 * 1000;
    const hourEnd = hourStart + oneHourMs;
    const hoursBeforeMs = hoursBeforeSession * 60 * 60 * 1000;

    return sessions.some(session => {
      const sessionStart = new Date(session.start_time).getTime();
      const sessionEnd = new Date(session.end_time).getTime();
      const windowStart = sessionStart - hoursBeforeMs;
      const windowEnd = sessionEnd + oneHourMs;
      return hourStart < windowEnd && hourEnd > windowStart;
    });
  }, [filterBySession, sessions, hoursBeforeSession]);

  // Sessions whose interval overlaps the hourly slot starting at `datetime`
  const getOverlappingSessions = React.useCallback((datetime: Date): Session[] => {
    if (sessions.length === 0) return [];

    const datetimeMs = datetime.getTime();
    const oneHourMs = 60 * 60 * 1000;
    const hourStart = datetimeMs;
    const hourEnd = datetimeMs + oneHourMs;

    return sessions.filter(session => {
      const sessionStart = new Date(session.start_time).getTime();
      const sessionEnd = new Date(session.end_time).getTime();
      return hourStart < sessionEnd && hourEnd > sessionStart;
    });
  }, [sessions]);

  // Check if a given calendar date has any sessions
  const dayHasSessions = React.useCallback((dateKey: string): boolean => {
    if (sessions.length === 0) return false;
    
    // Parse the dateKey (YYYY-MM-DD format) and create start/end of day in event timezone
    const [year, month, day] = dateKey.split('-').map(Number);
    
    return sessions.some(session => {
      const sessionStart = new Date(session.start_time);
      const sessionEnd = new Date(session.end_time);
      
      // Get the calendar date of session start and end in event timezone
      const startDateStr = sessionStart.toLocaleString('en-US', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
      const endDateStr = sessionEnd.toLocaleString('en-US', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
      
      const [startMonth, startDay, startYear] = startDateStr.split('/').map(Number);
      const [endMonth, endDay, endYear] = endDateStr.split('/').map(Number);
      
      // Check if the day falls within session's date range
      const dayDate = new Date(year, month - 1, day);
      const sessionStartDate = new Date(startYear, startMonth - 1, startDay);
      const sessionEndDate = new Date(endYear, endMonth - 1, endDay);
      
      return dayDate >= sessionStartDate && dayDate <= sessionEndDate;
    });
  }, [sessions, timezone]);

  // Ref for the cards container to attach non-passive wheel handler
  const cardsContainerRef = React.useRef<HTMLDivElement>(null);
  // Ref for the table container to attach non-passive wheel handler
  const tableContainerRef = React.useRef<HTMLDivElement>(null);
  // Map to track wheel handlers for text containers
  const textContainerHandlers = React.useRef<Map<string, { el: HTMLDivElement; handler: (e: WheelEvent) => void }>>(new Map());

  // Attach wheel event listener with { passive: false } to enable preventDefault for cards view
  React.useEffect(() => {
    const container = cardsContainerRef.current;
    if (!container || viewMode !== 'cards') return;

    const handleWheel = (e: WheelEvent) => {
      // Allow horizontal scrolling with mouse wheel
      if (e.deltaY !== 0 && container.scrollWidth > container.clientWidth) {
        e.preventDefault();
        container.scrollLeft += e.deltaY;
      }
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      container.removeEventListener('wheel', handleWheel);
    };
  }, [viewMode]);

  // Attach wheel event listener for table view horizontal scrolling
  React.useEffect(() => {
    const container = tableContainerRef.current;
    if (!container || viewMode !== 'table') return;

    const handleWheel = (e: WheelEvent) => {
      // Check if horizontal scrolling is possible
      const hasHorizontalScroll = container.scrollWidth > container.clientWidth;
      const hasVerticalScroll = container.scrollHeight > container.clientHeight;
      
      // If Shift is held, always scroll horizontally
      if (e.shiftKey && e.deltaY !== 0 && hasHorizontalScroll) {
        e.preventDefault();
        container.scrollLeft += e.deltaY;
        return;
      }
      
      // If there's horizontal overflow and no vertical overflow, convert vertical wheel to horizontal
      if (hasHorizontalScroll && !hasVerticalScroll && e.deltaY !== 0) {
        e.preventDefault();
        container.scrollLeft += e.deltaY;
        return;
      }
      
      // If there's horizontal overflow and we're at the vertical scroll boundaries, allow horizontal scrolling
      if (hasHorizontalScroll && e.deltaY !== 0) {
        const isAtTop = container.scrollTop === 0;
        const isAtBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 1;
        
        // If scrolling up at top or down at bottom, allow horizontal scrolling
        if ((isAtTop && e.deltaY < 0) || (isAtBottom && e.deltaY > 0)) {
          e.preventDefault();
          container.scrollLeft += e.deltaY;
        }
      }
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      container.removeEventListener('wheel', handleWheel);
    };
  }, [viewMode]);

  // Helper to create a ref callback for text containers that need independent vertical scrolling
  // This completely isolates the text area from the parent's horizontal scroll -
  // mouse wheel only affects the text area when hovering over it
  const createTextContainerRef = React.useCallback((key: string) => {
    return (el: HTMLDivElement | null) => {
      const handlers = textContainerHandlers.current;
      
      // Clean up previous handler for this key if exists
      const existing = handlers.get(key);
      if (existing) {
        existing.el.removeEventListener('wheel', existing.handler);
        handlers.delete(key);
      }
      
      if (el) {
        const handleWheel = (e: WheelEvent) => {
          // Always stop propagation when mouse is over the text area
          // This prevents the parent's horizontal scroll from being triggered
          e.stopPropagation();
        };
        
        el.addEventListener('wheel', handleWheel, { passive: true });
        handlers.set(key, { el, handler: handleWheel });
      }
    };
  }, []);

  // Get ALL forecasts issued BEFORE the selected time (matching legacy behavior)
  const validForecasts = React.useMemo(() => {
    if (!forecasts || forecasts.length === 0) return [];

    // If no date selected, use all forecasts
    if (!selectedDate) {
      return forecasts;
    }

    const selectedTime = selectedDate.getTime();
    
    // Filter forecasts issued before or at the selected time
    const valid = forecasts.filter(f => {
      const issueTime = new Date(f.value_date).getTime();
      return issueTime <= selectedTime;
    });

    return valid;
  }, [forecasts, selectedDate]);

  // Calculate next forecast update time (if available)
  const nextUpdateTime = React.useMemo(() => {
    if (!forecasts || forecasts.length === 0 || !selectedDate) return null;
    
    const selectedTime = selectedDate.getTime();
    
    // Find forecasts issued after the selected time
    const futureForecasts = forecasts.filter(f => {
      const issueTime = new Date(f.value_date).getTime();
      return issueTime > selectedTime;
    });
    
    if (futureForecasts.length === 0) return null;
    
    // Sort and get the earliest future forecast
    const sorted = futureForecasts.sort((a, b) => {
      return new Date(a.value_date).getTime() - new Date(b.value_date).getTime();
    });
    
    return new Date(sorted[0].value_date);
  }, [forecasts, selectedDate]);


  // Group hourly forecasts by day - use most recent forecast data for each day
  const forecastsByDay = React.useMemo(() => {
    if (validForecasts.length === 0) {
      return [];
    }

    // Get selected calendar date if available
    let selectedCalendarDate: number | null = null;
    if (selectedDate) {
      const dateStr = selectedDate.toLocaleString('en-US', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
      const [month, day, year] = dateStr.split('/').map(Number);
      selectedCalendarDate = new Date(year, month - 1, day).getTime();
    }

    // Map to store the most recent forecast data for each day
    const dayGroups = new Map<string, {
      forecasts: HourlyForecast[];
      forecastIssueTime: Date;
      valueDate: string; // Raw value_date from API (for exact backend matching)
      previousNight: string | null;
      weatherSummary: string | null;
      alertLevel: string | null;
    }>();
    
    // Process all valid forecasts
    validForecasts.forEach((forecast) => {
      const forecastIssueTime = new Date(forecast.value_date);
      
      if (!forecast.hourly_forecasts) return;
      
      forecast.hourly_forecasts.forEach((hf: HourlyForecast) => {
        const datetimeValue = typeof hf.forecast_datetime === 'object' && hf.forecast_datetime !== null && 'value' in hf.forecast_datetime
          ? (hf.forecast_datetime as { value: string }).value
          : hf.forecast_datetime;
        const datetime = new Date(datetimeValue);
        
        // Get calendar date in event timezone
        const dateStr = datetime.toLocaleString('en-US', {
          timeZone: timezone,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        });
        const [month, day, year] = dateStr.split('/').map(Number);
        const calendarDate = new Date(year, month - 1, day);
        const calendarDateKey = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        
        // Filter: only include days from selected date onwards
        if (selectedCalendarDate !== null) {
          if (calendarDate.getTime() < selectedCalendarDate) {
            return;
          }
        }
        
        // Check if we already have data for this day
        const existing = dayGroups.get(calendarDateKey);
        
        // If no data yet, or if this forecast is more recent, use it
        if (!existing || forecastIssueTime.getTime() > existing.forecastIssueTime.getTime()) {
          // Look up per-day metadata if available, fall back to root-level for legacy data
          const dayMeta = forecast.daily_metadata?.find(dm => dm.date === calendarDateKey);
          
          dayGroups.set(calendarDateKey, {
            forecasts: [hf],
            forecastIssueTime: forecastIssueTime,
            valueDate: forecast.value_date,
            previousNight: dayMeta?.previous_night ?? forecast.previous_night ?? null,
            weatherSummary: dayMeta?.weather_summary ?? forecast.weather_summary ?? null,
            alertLevel: dayMeta?.alert_level ?? forecast.alert_level ?? null,
          });
        } else if (forecastIssueTime.getTime() === existing.forecastIssueTime.getTime()) {
          // Same forecast, add this hourly data
          existing.forecasts.push(hf);
        }
      });
    });

    // Convert to array and sort by date
    return Array.from(dayGroups.entries())
      .map(([dateKey, data]): ForecastDayData => ({
        dateKey,
        date: new Date(dateKey),
        forecastIssueTime: data.forecastIssueTime,
        valueDate: data.valueDate,
        previousNight: data.previousNight,
        weatherSummary: data.weatherSummary,
        alertLevel: data.alertLevel,
        forecasts: data.forecasts.sort((a, b) => {
          const dateA = new Date(typeof a.forecast_datetime === 'object' && a.forecast_datetime !== null && 'value' in a.forecast_datetime
            ? (a.forecast_datetime as { value: string }).value
            : a.forecast_datetime);
          const dateB = new Date(typeof b.forecast_datetime === 'object' && b.forecast_datetime !== null && 'value' in b.forecast_datetime
            ? (b.forecast_datetime as { value: string }).value
            : b.forecast_datetime);
          return dateA.getTime() - dateB.getTime();
        }),
      }))
      .sort((a, b) => a.date.getTime() - b.date.getTime());
  }, [validForecasts, selectedDate, timezone]);

  // Prepare dynamic table data - store raw parameters for each time slot
  const tableData = React.useMemo(() => {
    if (forecastsByDay.length === 0) return { timeSlots: [] as DynamicTableTimeSlot[] };
    
    // Extract all time slots with their parameters
    const timeSlots: DynamicTableTimeSlot[] = forecastsByDay.flatMap(dayData => {
      return dayData.forecasts.map((hf: HourlyForecast) => {
        const datetimeValue = typeof hf.forecast_datetime === 'object' && hf.forecast_datetime !== null && 'value' in hf.forecast_datetime
          ? (hf.forecast_datetime as { value: string }).value
          : hf.forecast_datetime;
        const datetime = new Date(datetimeValue);
        
        // Filter out hours that have fully passed (keep the current hour visible)
        if (selectedDate && datetime.getTime() + 3600000 <= selectedDate.getTime()) {
          return null;
        }
        
        // Filter by session window if enabled
        if (!isWithinSessionWindow(datetime)) {
          return null;
        }
        
        // Format time and date
        const timeStr = datetime.toLocaleString('en-US', {
          timeZone: timezone,
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        });
        
        const dateStr = datetime.toLocaleString('en-US', {
          timeZone: timezone,
          weekday: 'short',
          month: 'short',
          day: '2-digit',
        });
        
        return {
          datetime: datetime.getTime(),
          datetimeIso: datetimeValue as string,
          valueDate: dayData.valueDate,
          timeStr,
          dateStr,
          parameters: hf.parameters,
        };
      }).filter((slot): slot is DynamicTableTimeSlot => slot !== null);
    });
    
    return { timeSlots };
  }, [forecastsByDay, selectedDate, timezone, isWithinSessionWindow]);

  // Get all parameter names present in the actual data
  const availableParams = React.useMemo(() => {
    const params = new Set<string>();
    forecastsByDay.forEach(day => {
      day.forecasts.forEach(hf => {
        hf.parameters.forEach(p => params.add(p.name.toLowerCase()));
      });
    });
    return params;
  }, [forecastsByDay]);

  // Save custom field order to localStorage
  const saveCustomFieldOrder = React.useCallback((fieldDbNames: string[]) => {
    try {
      localStorage.setItem('hourlyTableFieldOrder', JSON.stringify(fieldDbNames));
    } catch (error) {
      console.error('Error saving field order to localStorage:', error);
    }
  }, []);

  // Fields to display in TABLE view
  const tableDisplayFields = React.useMemo(() => {
    if (fieldsLoading || tableData.timeSlots.length === 0) return [];
    
    // Start with configured hourly table fields that have data
    const configuredFields = hourlyTableFields
      .filter(field => 
        availableParams.has(field.db_name.toLowerCase()) && 
        !isFieldCombined(field.db_name)
      );
    
    // Also include any parameters in data that aren't in configured fields
    // (for backward compatibility with legacy data)
    // BUT exclude fields that are marked as hidden in the database
    const configuredDbNames = new Set(hourlyTableFields.map(f => f.db_name.toLowerCase()));
    const unconfiguredParams = Array.from(availableParams)
      .filter(name => {
        // Skip if already configured
        if (configuredDbNames.has(name)) return false;
        // Skip if it's a combined field
        if (isFieldCombined(name)) return false;
        // Skip if the field exists in the database and is marked as hidden
        const field = fieldsByDbName.get(name);
        if (field && field.hidden) return false;
        return true;
      })
      .map(name => createDefaultField(name, null));
    
    const allFields = [...configuredFields, ...unconfiguredParams];
    
    // Apply custom order from localStorage if available
    // Read directly from localStorage to ensure we get the latest value when orderVersion changes
    let customOrder: string[] = [];
    try {
      const stored = localStorage.getItem('hourlyTableFieldOrder');
      if (stored) {
        customOrder = JSON.parse(stored);
      }
    } catch (error) {
      console.error('Error loading field order from localStorage:', error);
    }
    
    if (customOrder.length > 0) {
      // Create a map for quick lookup
      const fieldMap = new Map(allFields.map(f => [f.db_name.toLowerCase(), f]));
      
      // Build ordered array based on custom order
      const orderedFields: ForecastField[] = [];
      const remainingFields = new Set(allFields.map(f => f.db_name.toLowerCase()));
      
      // Add fields in custom order
      customOrder.forEach(dbName => {
        const lowerDbName = dbName.toLowerCase();
        const field = fieldMap.get(lowerDbName);
        if (field) {
          orderedFields.push(field);
          remainingFields.delete(lowerDbName);
        }
      });
      
      // Add any remaining fields that weren't in the custom order
      remainingFields.forEach(dbName => {
        const field = fieldMap.get(dbName);
        if (field) {
          orderedFields.push(field);
        }
      });
      
      return orderedFields;
    }
    
    return allFields;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hourlyTableFields, fieldsLoading, availableParams, tableData, createDefaultField, orderVersion, fieldsByDbName]);

  // Fields to display in CARDS view
  const cardsDisplayFields = React.useMemo(() => {
    if (fieldsLoading) return [];
    
    // Start with configured hourly cards fields that have data
    const configuredFields = hourlyCardsFields
      .filter(field => 
        availableParams.has(field.db_name.toLowerCase()) && 
        !isFieldCombined(field.db_name)
      );
    
    // Also include any parameters in data that aren't in configured fields
    // BUT exclude fields that are marked as hidden in the database
    const configuredDbNames = new Set(hourlyCardsFields.map(f => f.db_name.toLowerCase()));
    const unconfiguredParams = Array.from(availableParams)
      .filter(name => {
        // Skip if already configured
        if (configuredDbNames.has(name)) return false;
        // Skip if it's a combined field
        if (isFieldCombined(name)) return false;
        // Skip if the field exists in the database and is marked as hidden
        const field = fieldsByDbName.get(name);
        if (field && field.hidden) return false;
        return true;
      })
      .map(name => createDefaultField(name, null));
    
    return [...configuredFields, ...unconfiguredParams];
  }, [hourlyCardsFields, fieldsLoading, availableParams, createDefaultField, fieldsByDbName]);

  // Fields to skip in card rendering (handled specially or combined with others)
  const cardSkipFields = React.useMemo(() => 
    new Set(['track_temp_min', 'track_temp_max']),
  []);

  // Get fields for card rendering, sorted by position
  const sortedCardsFields = React.useMemo(() => {
    // Sort by position (nulls last)
    return [...cardsDisplayFields].sort((a, b) => {
      if (a.position === null && b.position === null) return 0;
      if (a.position === null) return 1;
      if (b.position === null) return -1;
      return a.position - b.position;
    });
  }, [cardsDisplayFields]);

  // Separate weather_code from other fields for card rendering
  const weatherCodeField = React.useMemo(() => 
    sortedCardsFields.find(f => f.db_name === 'weather_code'),
  [sortedCardsFields]);
  
  const otherCardFields = React.useMemo(() => 
    sortedCardsFields.filter(f => 
      f.db_name !== 'weather_code' && 
      !cardSkipFields.has(f.db_name)
    ),
  [sortedCardsFields, cardSkipFields]);

  // Group fields by card_group for cards view
  const groupedCardsFields = React.useMemo(() => {
    const groups: { groupName: string | null; fields: ForecastField[] }[] = [];
    const processedGroups = new Set<string>();
    
    otherCardFields.forEach(field => {
      if (field.card_group) {
        // If this group hasn't been processed yet, collect all fields in this group
        if (!processedGroups.has(field.card_group)) {
          processedGroups.add(field.card_group);
          const groupFields = otherCardFields.filter(f => f.card_group === field.card_group);
          groups.push({ groupName: field.card_group, fields: groupFields });
        }
      } else {
        // Ungrouped field - render individually
        groups.push({ groupName: null, fields: [field] });
      }
    });
    
    return groups;
  }, [otherCardFields]);

  // Check if we have legacy track temp fields (for combined rendering)
  const hasTrackTempMinMax = React.useMemo(() => {
    return availableParams.has('track_temp_min') || availableParams.has('track_temp_max');
  }, [availableParams]);

  // Get the most recent forecast issue time for the Issued label
  const latestForecastIssueTime = React.useMemo(() => {
    if (validForecasts.length === 0) return null;
    const sorted = [...validForecasts].sort((a, b) => 
      new Date(b.value_date).getTime() - new Date(a.value_date).getTime()
    );
    return new Date(sorted[0].value_date);
  }, [validForecasts]);

  if (validForecasts.length === 0) {
    return (
      <Card>
        <Text type="secondary">
          No forecast was issued before the selected time
        </Text>
      </Card>
    );
  }

  if (forecastsByDay.length === 0) {
    return (
      <Card>
        <Text type="secondary">No hourly forecast data available for the selected date</Text>
      </Card>
    );
  }

  // ==========================================
  // SVG Visualization Helpers
  // ==========================================
  
  const renderSparkline = (values: (number | null)[], color: string, height = 30) => {
    const validValues = values.filter((v): v is number => v !== null);
    if (validValues.length === 0) return null;
    
    const max = Math.max(...validValues);
    const min = Math.min(...validValues);
    const range = max - min || 1;
    
    const points = values.map((v, i) => {
      if (v === null) return null;
      const x = (i / (values.length - 1)) * 100;
      const y = ((v - min) / range) * 100;
      return `${x},${100 - y}`;
    }).filter(Boolean).join(' ');
    
    return (
      <svg 
        width="100%" 
        height={height} 
        viewBox="0 0 100 100" 
        preserveAspectRatio="none"
        style={{ display: 'block' }}
      >
        <polyline
          points={points}
          fill="none"
          stroke={color}
          strokeWidth="2"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
    );
  };

  // ==========================================
  // Dynamic Row Renderers
  // ==========================================

  // Helper to check if a time slot is the first slot of a new day
  const isFirstSlotOfDay = (idx: number): boolean => {
    if (idx === 0) return false; // First column is always the parameter name
    if (idx === 1) return true; // First data column is always start of first day
    const currentSlot = tableData.timeSlots[idx - 1];
    const previousSlot = tableData.timeSlots[idx - 2];
    return currentSlot.dateStr !== previousSlot.dateStr;
  };

  // Common cell styles
  const getHeaderCellStyle = () => ({
    position: 'sticky' as const,
    left: 0,
    backgroundColor: isDark ? '#141414' : '#fafafa',
    padding: '8px',
    borderBottom: `1px solid ${isDark ? '#434343' : '#f0f0f0'}`,
    fontWeight: 500,
    minWidth: '200px',
    width: '200px',
    zIndex: 1,
  });

  const getDataCellStyle = () => ({
    padding: '6px',
    borderBottom: `1px solid ${isDark ? '#434343' : '#f0f0f0'}`,
    textAlign: 'center' as const,
  });

  const getTimeHeaderCellStyle = (isFirstOfDay: boolean = false) => ({
    position: 'sticky' as const,
    top: 0,
    backgroundColor: isDark ? '#1f1f1f' : '#ffffff',
    padding: '8px',
    borderBottom: `2px solid ${isDark ? '#434343' : '#d9d9d9'}`,
    borderLeft: isFirstOfDay ? `2px solid ${isDark ? '#595959' : '#bfbfbf'}` : undefined,
    textAlign: 'center' as const,
    fontWeight: 600,
    minWidth: '120px',
    zIndex: 2,
  });

  // Weather code row (special handling)
  const renderWeatherCodeRow = (field: ForecastField) => (
    <tr key={field.db_name}>
      <td style={getHeaderCellStyle()}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {getFieldIcon(field.db_name, { size: 24 })} {field.display_name}
        </span>
      </td>
      {tableData.timeSlots.map((slot, idx) => {
        const param = getParameterValue(slot.parameters, field.db_name);
        const slotDatetime = new Date(slot.datetime);
        const weatherCode = getWeatherCodeForHourly(param.value, slotDatetime, timezone);
        return (
          <td key={idx} style={getDataCellStyle()}>
            <WeatherIconWithFallback iconPath={weatherCode.icon} size={36} />
          </td>
        );
      })}
    </tr>
  );

  // Sparkline row (temperature-like fields)
  const renderSparklineRow = (field: ForecastField, config: FieldRenderConfig) => {
    const values = tableData.timeSlots.map(slot => {
      const param = getParameterValue(slot.parameters, field.db_name);
      return param.value ? parseFloat(param.value) : null;
    });
    
    const validValues = values.filter((v): v is number => v !== null);
    if (validValues.length === 0) {
      return renderValueRow(field); // Fall back to simple value row
    }
    
    const globalMin = Math.min(...validValues);
    const globalMax = Math.max(...validValues);
    const range = globalMax - globalMin || 1;
    const color = config.color || '#1890ff';
    
    return (
      <tr key={field.db_name}>
        <td style={{ ...getHeaderCellStyle(), verticalAlign: 'middle' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            {getFieldIcon(field.db_name, { size: 24 })} {field.display_name} {field.unit ? `(${field.unit})` : ''}
          </span>
        </td>
        <td
          colSpan={tableData.timeSlots.length}
          style={{
            padding: '6px',
            borderBottom: `1px solid ${isDark ? '#434343' : '#f0f0f0'}`,
            position: 'relative',
            height: '50px',
          }}
        >
          <div style={{ position: 'relative', width: '100%', height: '100%' }}>
            {/* Sparkline background */}
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}>
              {renderSparkline(values, color, 40)}
            </div>
            {/* Values overlay */}
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex' }}>
              {values.map((value, idx) => {
                const valuePos = value !== null 
                  ? ((globalMax - value) / range) * 100 
                  : 50;
                
                return (
                  <div
                    key={idx}
                    style={{
                      flex: 1,
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {value !== null && (
                      <div
                        style={{
                          position: 'absolute',
                          top: `${valuePos}%`,
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          fontSize: '12px',
                          fontWeight: 600,
                          color: color,
                          backgroundColor: isDark ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.8)',
                          padding: '2px 4px',
                          borderRadius: 3,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {Math.round(value)}°C
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </td>
      </tr>
    );
  };

  // Bar indicator row (percentage fields like rain probability)
  const renderBarRow = (field: ForecastField, config: FieldRenderConfig) => (
    <tr key={field.db_name}>
      <td style={getHeaderCellStyle()}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {getFieldIcon(field.db_name, { size: 24 })} {field.display_name} {field.unit ? `(${field.unit})` : ''}
        </span>
      </td>
      {tableData.timeSlots.map((slot, idx) => {
        const param = getParameterValue(slot.parameters, field.db_name);
        const value = parsePercentageValue(param.value);
        const color = config.color || '#1890ff';
        
        return (
          <td key={idx} style={getDataCellStyle()}>
            {value !== null ? (
              <div style={{ width: 70, margin: '0 auto' }}>
                <Progress 
                  percent={value} 
                  size="small"
                  strokeColor={color}
                  trailColor={isDark ? '#303030' : '#f0f0f0'}
                  format={(percent) => <span style={{ fontSize: 11, color: isDark ? '#fff' : '#000' }}>{percent}%</span>}
                />
              </div>
            ) : (
              <Text type="secondary">N/A</Text>
            )}
          </td>
        );
      })}
    </tr>
  );

  // Wind row (with color coding, direction shown in separate row)
  // Note: Wind values are stored in m/s but converted to km/h for display
  const renderWindRow = (field: ForecastField, config: FieldRenderConfig) => {
    return (
      <tr key={field.db_name}>
        <td style={getHeaderCellStyle()}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            {getFieldIcon(field.db_name, { size: 24 })} {field.display_name} (km/h)
          </span>
        </td>
        {tableData.timeSlots.map((slot, idx) => {
          const param = getParameterValue(slot.parameters, field.db_name);
          const speedNum = parseWindValue(param.value); // Converts m/s to km/h
          const bgColor = config.getColor ? config.getColor(speedNum) : 'transparent';
          
          return (
            <td
              key={idx}
              style={{
                ...getDataCellStyle(),
                padding: '8px',
                backgroundColor: bgColor,
              }}
            >
              {speedNum !== null ? (
                <div style={{ fontSize: '12px', color: bgColor !== 'transparent' ? '#fff' : 'inherit' }}>
                  {Math.round(speedNum)} km/h
                </div>
              ) : (
                <Text type="secondary">N/A</Text>
              )}
            </td>
          );
        })}
      </tr>
    );
  };

  // Direction row (shows arrow instead of numeric value)
  const renderDirectionRow = (field: ForecastField) => (
    <tr key={field.db_name}>
      <td style={getHeaderCellStyle()}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {getFieldIcon(field.db_name, { size: 24 })} {field.display_name}
        </span>
      </td>
      {tableData.timeSlots.map((slot, idx) => {
        const param = getParameterValue(slot.parameters, field.db_name);
        const windSpeedParam = getParameterValue(slot.parameters, 'wind_speed');
        const windSpeed = windSpeedParam.value ? parseFloat(windSpeedParam.value) * 3.6 : null; // Convert m/s to km/h
        
        return (
          <td key={idx} style={getDataCellStyle()}>
            {param.value ? (
              <div style={{ 
                textAlign: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <WindDirectionIcon
                  direction={param.value}
                  windSpeed={windSpeed}
                  size={16}
                  showAngle={true}
                />
              </div>
            ) : (
              <Text type="secondary">N/A</Text>
            )}
          </td>
        );
      })}
    </tr>
  );

  // Helper to check if a field is a wind field that needs m/s to km/h conversion
  const isWindField = (dbName: string): boolean => {
    return dbName.includes('wind_speed') || dbName.includes('wind_gust');
  };

  // Simple value row (default for most fields)
  // Note: Wind fields are stored in m/s but displayed in km/h
  const renderValueRow = (field: ForecastField) => {
    const needsWindConversion = isWindField(field.db_name);
    const displayUnit = needsWindConversion ? 'km/h' : field.unit;
    
    return (
      <tr key={field.db_name}>
        <td style={getHeaderCellStyle()}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            {getFieldIcon(field.db_name, { size: 24 })} {field.display_name} {displayUnit ? `(${displayUnit})` : ''}
          </span>
        </td>
        {tableData.timeSlots.map((slot, idx) => {
          const param = getParameterValue(slot.parameters, field.db_name);
          
          // Convert wind values from m/s to km/h for display
          let displayValue = param.value;
          if (param.value !== null && needsWindConversion) {
            const converted = parseWindValue(param.value);
            displayValue = converted !== null ? Math.round(converted).toString() : param.value;
          } else if (param.value !== null && field.db_name.includes('pressure')) {
            // Round pressure values to integers (no decimals)
            const numValue = parseFloat(param.value);
            if (!isNaN(numValue)) {
              displayValue = Math.round(numValue).toString();
            }
          }
          
          return (
            <td key={idx} style={getDataCellStyle()}>
              {param.value !== null ? (
                <div style={{ fontSize: '13px' }}>
                  {displayValue}{displayUnit && !String(displayValue).includes(displayUnit) ? ` ${displayUnit}` : ''}
                </div>
              ) : (
                <Text type="secondary">N/A</Text>
              )}
            </td>
          );
        })}
      </tr>
    );
  };

  // Main row renderer - dispatches to specific renderers based on field config
  const renderFieldRow = (field: ForecastField) => {
    const config = getFieldRenderConfig(field);
    
    switch (config.renderType) {
      case 'weatherCode':
        return renderWeatherCodeRow(field);
      case 'sparkline':
        return renderSparklineRow(field, config);
      case 'bar':
        return renderBarRow(field, config);
      case 'wind':
        return renderWindRow(field, config);
      case 'direction':
        return renderDirectionRow(field);
      default:
        return renderValueRow(field);
    }
  };

  // ==========================================
  // Cards View Rendering - Dynamic field-based
  // ==========================================

  // Helper: wraps a displayed value with EditableParameterPopover when editing is enabled
  const wrapWithEditable = (
    element: React.ReactNode,
    field: ForecastField,
    rawValue: string | null,
    editContext?: { valueDate: string; forecastDatetimeIso: string },
    storageUnit?: string | null,
  ): React.ReactNode => {
    if (!isEditable || !onSaveParameter || !editContext || rawValue === null) return element;
    return (
      <EditableParameterPopover
        displayName={field.display_name}
        currentValue={rawValue}
        unit={storageUnit ?? field.unit}
        onSave={(newValue) => onSaveParameter(editContext.valueDate, editContext.forecastDatetimeIso, field.db_name, newValue)}
      >
        {element}
      </EditableParameterPopover>
    );
  };

  // Render a single field value for the card
  const renderCardFieldValue = (
    field: ForecastField,
    parameters: HourlyForecastParameter[],
    isFirst: boolean,
    editContext?: { valueDate: string; forecastDatetimeIso: string },
  ): React.ReactNode => {
    const config = getFieldRenderConfig(field);
    const param = getParameterValue(parameters, field.db_name);
    
    // Handle wind fields (direction shown separately)
    // Note: Wind values are stored in m/s but converted to km/h for display
    if (config.renderType === 'wind') {
      const windValue = parseWindValue(param.value); // Converts m/s to km/h
      if (windValue === null) return null;
      
      const valueEl = <span style={{ cursor: 'help' }}>{Math.round(windValue)} km/h</span>;
      return (
        <div style={{ fontSize: isFirst ? 18 : 11, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
          {isFirst ? null : getFieldIcon(field.db_name, { size: 24 })}
          <Tooltip title={field.display_name}>
            {wrapWithEditable(valueEl, field, param.value, editContext, 'm/s')}
          </Tooltip>
        </div>
      );
    }
    
    // Handle wind direction (show arrow)
    if (config.renderType === 'direction') {
      if (!param.value) return null;
      
      // Get wind speed for color coding
      const windSpeedParam = getParameterValue(parameters, 'wind_speed');
      const windSpeed = windSpeedParam.value ? parseWindValue(windSpeedParam.value) : null;
      
      const dirEl = (
        <span style={{ cursor: 'help', display: 'inline-flex', alignItems: 'center' }}>
          <WindDirectionIcon
            direction={param.value}
            windSpeed={windSpeed}
            size={isFirst ? 18 : 14}
            showAngle={true}
          />
        </span>
      );
      return (
        <div style={{ fontSize: isFirst ? 18 : 14, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
          {isFirst ? null : getFieldIcon(field.db_name, { size: 24 })}
          <Tooltip title={field.display_name}>
            {wrapWithEditable(dirEl, field, param.value, editContext, 'degrees')}
          </Tooltip>
        </div>
      );
    }
    
    // Handle bar-type fields (percentages)
    if (config.renderType === 'bar') {
      const value = parsePercentageValue(param.value);
      // Skip if value is null (but always show 0)
      if (!isFirst && value === null) return null;
      
      const barEl = <span style={{ cursor: 'help' }}>{value !== null ? `${Math.round(value)}%` : 'N/A'}</span>;
      return (
        <div style={{ fontSize: isFirst ? 18 : 11, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
          {getFieldIcon(field.db_name, { size: 24 })}
          <Tooltip title={field.display_name}>
            {wrapWithEditable(barEl, field, param.value, editContext)}
          </Tooltip>
        </div>
      );
    }
    
    // Handle temperature/sparkline fields
    if (config.renderType === 'sparkline') {
      const value = param.value ? parseFloat(param.value) : null;
      if (!isFirst && value === null) return null;
      
      const tempEl = <span style={{ cursor: 'help' }}>{value !== null ? `${Math.round(value)}°C` : 'N/A'}</span>;
      if (isFirst) {
        return (
          <div style={{ fontSize: 18, fontWeight: 'bold', marginBottom: 4 }}>
            <Tooltip title={field.display_name}>
              {wrapWithEditable(tempEl, field, param.value, editContext)}
            </Tooltip>
          </div>
        );
      }
      
      return (
        <div style={{ fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
          {getFieldIcon(field.db_name, { size: 24 })}
          <Tooltip title={field.display_name}>
            {wrapWithEditable(tempEl, field, param.value, editContext)}
          </Tooltip>
        </div>
      );
    }
    
    // Default value rendering
    const value = param.value;
    if (!isFirst && value === null) return null;
    
    // Check if this is a wind field that needs m/s to km/h conversion
    const needsWindConversion = isWindField(field.db_name);
    
    // Format numeric values nicely (e.g., round pressure values)
    let formattedValue: string | null = value;
    if (value !== null) {
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        // Convert wind values from m/s to km/h
        const displayNumValue = needsWindConversion ? numValue * 3.6 : numValue;
        
        // Round to 1 decimal for most values, integer for pressure
        if (field.db_name.includes('pressure')) {
          formattedValue = Math.round(displayNumValue).toString();
        } else {
          formattedValue = displayNumValue % 1 === 0 ? displayNumValue.toString() : displayNumValue.toFixed(1);
        }
      }
    }
    
    // Use km/h for wind fields, otherwise use field.unit
    const displayUnit = needsWindConversion ? 'km/h' : field.unit;
    const displayValue = formattedValue !== null 
      ? (displayUnit ? `${formattedValue} ${displayUnit}` : formattedValue)
      : 'N/A';
    
    const storageUnit = needsWindConversion ? 'm/s' : field.unit;
    const defaultEl = <span style={{ cursor: 'help' }}>{displayValue}</span>;
    if (isFirst) {
      return (
        <div style={{ fontSize: 18, fontWeight: 'bold', marginBottom: 4 }}>
          <Tooltip title={field.display_name}>
            {wrapWithEditable(defaultEl, field, param.value, editContext, storageUnit)}
          </Tooltip>
        </div>
      );
    }
    
    return (
      <div style={{ fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
        {getFieldIcon(field.db_name, { size: 24 })}
        <Tooltip title={field.display_name}>
          {wrapWithEditable(defaultEl, field, param.value, editContext, storageUnit)}
        </Tooltip>
      </div>
    );
  };

  // Render legacy track temp min/max combined
  const renderTrackTempCombined = (parameters: HourlyForecastParameter[]): React.ReactNode => {
    const trackTempMax = getParameterValue(parameters, 'track_temp_max');
    const trackTempMin = getParameterValue(parameters, 'track_temp_min');
    
    const maxVal = trackTempMax.value ? parseFloat(trackTempMax.value) : null;
    const minVal = trackTempMin.value ? parseFloat(trackTempMin.value) : null;
    
    if (maxVal === null && minVal === null) return null;
    
    let display: string;
    if (minVal !== null && maxVal !== null) {
      display = `${Math.round(minVal)}-${Math.round(maxVal)}°C`;
    } else if (maxVal !== null) {
      display = `${Math.round(maxVal)}°C`;
    } else if (minVal !== null) {
      display = `${Math.round(minVal)}°C`;
    } else {
      return null;
    }
    
    return (
      <div style={{ fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
        {getFieldIcon('track_temp_max', { size: 24 })}
        <Tooltip title="Track Temperature">
          <span style={{ cursor: 'help' }}>{display}</span>
        </Tooltip>
      </div>
    );
  };

  // Render a group of fields (or single ungrouped field) in the card
  const renderCardFieldGroup = (
    groupName: string | null,
    fields: ForecastField[],
    parameters: HourlyForecastParameter[],
    isFirstGroup: boolean,
    editContext?: { valueDate: string; forecastDatetimeIso: string },
  ): React.ReactNode => {
    // Filter out fields with null values
    const fieldsWithValues = fields.filter(f => {
      const param = getParameterValue(parameters, f.db_name);
      return param.value !== null;
    });
    
    if (fieldsWithValues.length === 0) return null;
    
    // For ungrouped fields (single field), render normally
    if (!groupName || fieldsWithValues.length === 1) {
      return renderCardFieldValue(fieldsWithValues[0], parameters, isFirstGroup && !groupName, editContext);
    }
    
    // For grouped fields, render them together with icon and separator
    // Build values for each field in the group, keeping field info for tooltips
    type PartWithField = { value: string | number; field: ForecastField; isDirection: boolean; windSpeed: number | null; rawValue: string | null };
    const partsWithFields: PartWithField[] = [];
    
    fieldsWithValues.forEach(field => {
      const param = getParameterValue(parameters, field.db_name);
      const fieldConfig = getFieldRenderConfig(field);
      
      let displayValue: string | number | null = null;
      let isDirection = false;
      let windSpeed: number | null = null;
      
      // Handle different render types
      if (fieldConfig.renderType === 'wind') {
        const windValue = parseWindValue(param.value);
        displayValue = windValue !== null ? `${Math.round(windValue)} km/h` : null;
      } else if (fieldConfig.renderType === 'direction') {
        // For direction, we'll render it separately as a component
        isDirection = true;
        // Store the raw value (could be string or number)
        if (param.value !== null && param.value !== undefined) {
          // Convert to number if it's a numeric string, otherwise keep as string
          const numValue = typeof param.value === 'string' ? parseFloat(param.value) : param.value;
          displayValue = !isNaN(numValue as number) ? (typeof param.value === 'number' ? param.value : numValue) : param.value;
        }
        // Get wind speed for color coding
        const windSpeedParam = getParameterValue(parameters, 'wind_speed');
        windSpeed = windSpeedParam.value ? parseWindValue(windSpeedParam.value) : null;
      } else if (fieldConfig.renderType === 'bar') {
        const value = parsePercentageValue(param.value);
        displayValue = value !== null ? `${Math.round(value)}%` : null;
      } else if (fieldConfig.renderType === 'sparkline') {
        const value = param.value ? parseFloat(param.value) : null;
        displayValue = value !== null ? `${Math.round(value)}°C` : null;
      } else {
        // Default: show value with appropriate unit (km/h for wind, °C for temp, etc.)
        if (param.value === null) {
          displayValue = null;
        } else {
          const numValue = parseFloat(param.value);
          if (!isNaN(numValue)) {
            if (field.db_name.includes('pressure')) {
              displayValue = `${Math.round(numValue)} ${field.unit || 'hPa'}`;
            } else if (isWindField(field.db_name)) {
              const windValue = parseWindValue(param.value);
              displayValue = windValue !== null ? `${Math.round(windValue)} km/h` : null;
            } else if (field.unit === '°C' || field.unit === '°F' || field.db_name.includes('temp')) {
              displayValue = `${numValue % 1 === 0 ? numValue.toString() : numValue.toFixed(1)}°C`;
            } else {
              displayValue = numValue % 1 === 0 ? numValue.toString() : numValue.toFixed(1);
              if (field.unit) displayValue = `${displayValue} ${field.unit}`;
            }
          } else {
            displayValue = param.value;
          }
        }
      }
      
      if (displayValue !== null) {
        partsWithFields.push({ value: displayValue, field, isDirection, windSpeed, rawValue: param.value });
      }
    });
    
    if (partsWithFields.length === 0) return null;
    
    // Render each parameter in the group with its own icon (unit is already in displayValue)
    return (
      <div style={{ fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
        {partsWithFields.map((part, idx) => {
          const isDirectionArrow = part.isDirection;
          return (
            <React.Fragment key={idx}>
              {idx > 0 && <span style={{ opacity: 0.5, margin: '0 1px' }}>|</span>}
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                {!part.isDirection && getFieldIcon(part.field.db_name, { size: 24 })}
                <Tooltip title={part.field.display_name}>
                  {isDirectionArrow ? (
                    wrapWithEditable(
                      <span style={{ cursor: 'help', display: 'inline-flex', alignItems: 'center' }}>
                        <WindDirectionIcon
                          direction={part.value}
                          windSpeed={part.windSpeed}
                          size={14}
                          showAngle={true}
                        />
                      </span>,
                      part.field, part.rawValue, editContext, 'degrees'
                    )
                  ) : (
                    wrapWithEditable(
                      <span style={{ cursor: 'help' }}>{part.value}</span>,
                      part.field, part.rawValue, editContext,
                      isWindField(part.field.db_name) ? 'm/s' : part.field.unit
                    )
                  )}
                </Tooltip>
              </span>
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  const renderDayCard = (dayData: ForecastDayData) => {
    const { forecasts, forecastIssueTime, previousNight, weatherSummary, valueDate } = dayData;
    
    // Format date - show full format like "TUE, NOV 04"
    const dateStr = formatDateKeyForHeader(dayData.dateKey);

    // Process hourly data for this day
    const hourlyData = forecasts
      .map((hf: HourlyForecast) => {
        const datetimeValue = typeof hf.forecast_datetime === 'object' && hf.forecast_datetime !== null && 'value' in hf.forecast_datetime
          ? (hf.forecast_datetime as { value: string }).value
          : hf.forecast_datetime;
        const datetime = new Date(datetimeValue);
        
        // Format time in event timezone
        const time = datetime.toLocaleTimeString('en-US', {
          timeZone: timezone,
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        });
        
        // Get weather icon
        const weatherCodeParam = getParameterValue(hf.parameters, 'weather_code');
        const weatherCode = getWeatherCodeForHourly(weatherCodeParam.value, datetime, timezone);
        
        return {
          datetime,
          datetimeIso: datetimeValue as string,
          time,
          weatherIcon: weatherCode.icon,
          weatherCodeValue: weatherCodeParam.value,
          parameters: hf.parameters,
        };
      })
      .filter((data) => {
        // Filter out hours that have fully passed (keep the current hour visible)
        if (selectedDate && data.datetime.getTime() + 3600000 <= selectedDate.getTime()) {
          return false;
        }
        // Filter by session window if enabled
        return isWithinSessionWindow(data.datetime);
      });

    // Format the forecast issue time
    const issueTimeStr = forecastIssueTime.toLocaleString('en-US', {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      day: '2-digit',
      month: 'short',
      hour12: false,
    });

    return (
      <Card
        key={dayData.dateKey}
        style={{
          flex: '0 0 auto',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: isDark ? '#1f1f1f' : '#ffffff',
          borderColor: isDark ? '#434343' : '#d9d9d9',
          borderRadius: 0,
        }}
        styles={{ body: { display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, scrollbarWidth: 'thin' as const } }}
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Title level={5} style={{ margin: 0 }}>{dateStr}</Title>
              {(previousNight || weatherSummary) && (
                <Tooltip title="View full details">
                  <Button
                    type="text"
                    size="small"
                    icon={<FullscreenOutlined />}
                    onClick={() => setOpenModalDayKey(dayData.dateKey)}
                    style={{ 
                      padding: '0 4px',
                      height: 'auto',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  />
                </Tooltip>
              )}
            </div>
            <Text type="secondary" style={{ fontSize: '12px', fontWeight: 'normal' }}>
              Updated: {issueTimeStr}
            </Text>
          </div>
        }
      >
        {/* Previous Night and Weather Conditions - hidden in compact mode; collapsed by default with 2-line preview */}
        {!compactCards && (previousNight || weatherSummary) && (
          <div 
            ref={createTextContainerRef(dayData.dateKey)}
            style={{ 
              maxWidth: 'fit-content',
              flex: '0 0 auto',
              marginBottom: 8,
              paddingRight: 8,
            }}
          >
            {previousNight && (
              <div style={{ marginBottom: 8 }}>
                <CollapsibleText
                  content={previousNight}
                  isDark={isDark}
                  compact
                  defaultCollapsed
                  gradientRgb={isDark ? undefined : '255, 255, 255'}
                  header={<>
                    <Title level={5} style={{ marginTop: 0, marginBottom: 0, fontSize: '13px' }}>🌙 Previous Night</Title>
                    {isEditable && onSavePreviousNight && previousNight !== null && (
                      <EditableTextModal
                        title="Previous Night"
                        initialValue={previousNight}
                        onSave={(newText) => onSavePreviousNight(valueDate, newText, dayData.dateKey)}
                        buttonSize="small"
                        buttonStyle={{ marginTop: -4 }}
                      />
                    )}
                  </>}
                />
              </div>
            )}

            {weatherSummary && (
              <CollapsibleText
                content={weatherSummary}
                isDark={isDark}
                compact
                defaultCollapsed
                gradientRgb={isDark ? undefined : '255, 255, 255'}
                header={<>
                  <Title level={5} style={{ marginTop: 0, marginBottom: 0, fontSize: '13px' }}>☁️ Weather Conditions</Title>
                  {isEditable && onSaveWeatherSummary && weatherSummary !== null && (
                    <EditableTextModal
                      title="Weather Conditions"
                      initialValue={weatherSummary}
                      onSave={(newText) => onSaveWeatherSummary(valueDate, newText, dayData.dateKey)}
                      buttonSize="small"
                      buttonStyle={{ marginTop: -4 }}
                    />
                  )}
                </>}
              />
            )}
          </div>
        )}

        {/* Hourly Forecasts - stretches to fill remaining card height; scrolls horizontally */}
        <div
          style={{
            display: 'flex',
            alignItems: 'stretch',
            gap: 8,
            flex: '1 1 0',
            minHeight: 0,
            overflowX: 'auto',
            paddingBottom: 4,
          }}
        >
          {hourlyData.map((data, idx) => (
            <div
              key={idx}
              style={{
                flex: '0 0 auto',
                display: 'flex',
                flexDirection: 'column',
                textAlign: 'center',
                padding: '8px 8px',
                borderRadius: 8,
                minWidth: 90,
                height: '100%',
                overflowY: 'auto',
                backgroundColor: isDark ? '#141414' : '#f5f5f5',
                border: isDark ? '1px solid #434343' : '1px solid #d9d9d9',
                scrollbarWidth: 'thin',
              }}
            >
              {/* Time - with session name inline when in a session (e.g. 11:00 (FP1)) */}
              {(() => {
                const overlappingSessions = getOverlappingSessions(data.datetime);
                const isInSession = overlappingSessions.length > 0;
                const sessionNameStr = formatCardSessionParts(overlappingSessions, data.datetime, timezone);
                const timeLabel = isInSession ? `${data.time} (${sessionNameStr})` : data.time;

                return (
                  <div
                    style={{
                      fontSize: 12,
                      marginBottom: 4,
                      display: 'inline-block',
                      padding: isInSession ? '2px 8px' : undefined,
                      borderRadius: isInSession ? 12 : undefined,
                      border: isInSession ? '2px solid #1890ff' : undefined,
                      backgroundColor: isInSession ? (isDark ? 'rgba(24, 144, 255, 0.15)' : 'rgba(24, 144, 255, 0.1)') : undefined,
                    }}
                  >
                    <Text
                      type="secondary"
                      style={{
                        fontWeight: isInSession ? 600 : undefined,
                        color: isInSession ? '#1890ff' : undefined,
                      }}
                    >
                      {timeLabel}
                    </Text>
                  </div>
                );
              })()}
              
              {/* Weather Icon (if weather_code field is enabled) */}
              {weatherCodeField && (
                <div style={{ marginBottom: 4 }}>
                  {isEditable && onSaveParameter ? (
                    <WeatherIconPicker
                      currentCode={data.weatherCodeValue}
                      codeType="hourly"
                      onSelect={(code) => onSaveParameter(valueDate, data.datetimeIso, 'weather_code', code)}
                    >
                      <WeatherIconWithFallback iconPath={data.weatherIcon} size={48} />
                    </WeatherIconPicker>
                  ) : (
                    <WeatherIconWithFallback iconPath={data.weatherIcon} size={48} />
                  )}
                </div>
              )}
              
              {/* Dynamic fields - grouped by card_group */}
              {fieldsLoading ? (
                <div style={{ padding: '10px 0' }}>
                  <Spin size="small" />
                </div>
              ) : (
                <>
                  {groupedCardsFields.map((group, groupIdx) => (
                    <React.Fragment key={group.groupName || group.fields[0].db_name}>
                      {renderCardFieldGroup(group.groupName, group.fields, data.parameters, groupIdx === 0,
                        isEditable && onSaveParameter ? { valueDate, forecastDatetimeIso: data.datetimeIso } : undefined
                      )}
                    </React.Fragment>
                  ))}
                  
                  {/* Legacy track temp min/max combined (if present in data) */}
                  {hasTrackTempMinMax && renderTrackTempCombined(data.parameters)}
                </>
              )}
            </div>
          ))}
        </div>
      </Card>
    );
  };

  // ==========================================
  // Main Render
  // ==========================================

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* Controls Container - Issued Label + View Toggle */}
      <Card
        size="small"
        style={{
          marginBottom: 0,
          backgroundColor: isDark ? '#1f1f1f' : '#fafafa',
          borderColor: isDark ? '#434343' : '#d9d9d9',
          flexShrink: 0,
          borderRadius: 0,
        }}
        styles={{ body: { padding: '4px 12px' } }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
            {latestForecastIssueTime && (
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Issued: {latestForecastIssueTime.toLocaleString(undefined, {
                  timeZone: timezone,
                  dateStyle: 'medium',
                  timeStyle: 'short',
                })}
              </Text>
            )}
            {nextUpdateTime && (
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Next update: {nextUpdateTime.toLocaleString(undefined, {
                  timeZone: timezone,
                  dateStyle: 'medium',
                  timeStyle: 'short',
                })}
              </Text>
            )}
            {statusExtra}
          </div>
          <Space wrap>
            {/* Date filter - opt-in switch + multi-select */}
            {eventDateRange.length > 0 && onDateFilterEnabledChange && (
              <Space size="small">
                <Tooltip title="Filter forecast data by date (can impact performance when enabled)">
                  <Space size={4}>
                    <CalendarOutlined style={{ color: dateFilterEnabled ? '#1890ff' : undefined }} />
                    <Switch
                      size="small"
                      checked={dateFilterEnabled}
                      onChange={onDateFilterEnabledChange}
                    />
                    <span style={{ fontSize: '12px', color: isDark ? '#d9d9d9' : '#595959' }}>
                      Filter by date
                    </span>
                  </Space>
                </Tooltip>
                {dateFilterEnabled && onFilterDatesChange && (
                  <>
                    <Select
                      mode="multiple"
                      value={selectedFilterDates}
                      onChange={onFilterDatesChange}
                      style={{ minWidth: 180, maxWidth: 320 }}
                      placeholder="All dates"
                      maxTagCount="responsive"
                      size="small"
                      options={eventDateRange.map(d => ({
                        value: d,
                        label: new Date(d + 'T12:00:00').toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' }),
                      }))}
                    />
                    {selectedFilterDates.length < eventDateRange.length && (
                      <Button
                        type="link"
                        size="small"
                        onClick={() => onFilterDatesChange([...eventDateRange])}
                        style={{ padding: '0 4px', height: 'auto', fontSize: 12 }}
                      >
                        All
                      </Button>
                    )}
                  </>
                )}
              </Space>
            )}
            {/* Session Filter Controls */}
            {sessions.length > 0 && (
              <Space size="small">
                <Tooltip title="Filter to show only hours relevant to sessions">
                  <Space size={4}>
                    <FilterOutlined style={{ color: filterBySession ? '#1890ff' : undefined }} />
                    <Switch
                      size="small"
                      checked={filterBySession}
                      onChange={handleSessionFilterChange}
                    />
                    <span style={{ fontSize: '12px', color: isDark ? '#d9d9d9' : '#595959' }}>
                      Sessions Only
                    </span>
                  </Space>
                </Tooltip>
                {filterBySession && (
                  <Tooltip title="Hours before session start to include">
                    <Space size={4}>
                      <InputNumber
                        size="small"
                        min={0}
                        max={12}
                        value={hoursBeforeSession}
                        onChange={(value) => setHoursBeforeSession(value ?? 2)}
                        style={{ width: 60 }}
                      />
                      <span style={{ fontSize: '12px', color: isDark ? '#d9d9d9' : '#595959' }}>
                        hrs before
                      </span>
                    </Space>
                  </Tooltip>
                )}
              </Space>
            )}
            {/* Compact Cards Toggle - only show in cards view */}
            {viewMode === 'cards' && (
              <Tooltip title="Hide Previous Night and Weather Conditions to see more hours">
                <Space size={4}>
                  <Switch
                    size="small"
                    checked={compactCards}
                    onChange={setCompactCards}
                  />
                  <span style={{ fontSize: '12px', color: isDark ? '#d9d9d9' : '#595959' }}>
                    Compact
                  </span>
                </Space>
              </Tooltip>
            )}
            {viewMode === 'table' && (
              <Button
                size="small"
                icon={<OrderedListOutlined />}
                onClick={() => setIsReorderModalOpen(true)}
              >
                Reorder Fields
              </Button>
            )}
            <Space.Compact>
              <Button
                size="small"
                type={viewMode === 'cards' ? 'primary' : 'default'}
                icon={<AppstoreOutlined />}
                onClick={() => setViewMode('cards')}
              >
                Cards
              </Button>
              <Button
                size="small"
                type={viewMode === 'table' ? 'primary' : 'default'}
                icon={<TableOutlined />}
                onClick={() => setViewMode('table')}
              >
                Table
              </Button>
              <Button
                size="small"
                type={viewMode === 'chart' ? 'primary' : 'default'}
                icon={<LineChartOutlined />}
                onClick={() => setViewMode('chart')}
              >
                Chart
              </Button>
            </Space.Compact>
          </Space>
        </div>
      </Card>

      {/* Table View - Dynamic Fields */}
      {viewMode === 'table' && (
        <div
          style={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            border: `1px solid ${isDark ? '#434343' : '#d9d9d9'}`,
            borderRadius: 8,
            backgroundColor: isDark ? '#1f1f1f' : '#ffffff',
          }}
        >
          {fieldsLoading ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <Spin tip="Loading field configuration..." />
            </div>
          ) : (
            <div 
              ref={tableContainerRef}
              style={{ 
                flex: 1, 
                minHeight: 0, 
                overflow: 'auto',
                scrollbarWidth: 'thin',
              }}
            >
              <table
                style={{
                  width: 'max-content',
                  minWidth: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '13px',
                  color: isDark ? '#d9d9d9' : '#262626',
                }}
              >
                <thead>
                  {/* Session name row */}
                  {sessions.length > 0 && (
                    <tr>
                      <th
                        style={{
                          position: 'sticky',
                          top: 0,
                          left: 0,
                          backgroundColor: isDark ? '#1f1f1f' : '#ffffff',
                          padding: '4px 8px',
                          borderBottom: `1px solid ${isDark ? '#434343' : '#e8e8e8'}`,
                          textAlign: 'left',
                          fontWeight: 500,
                          minWidth: '200px',
                          width: '200px',
                          zIndex: 3,
                        }}
                      >
                        {''}
                      </th>
                      {tableData.timeSlots.map((slot, idx) => {
                        const slotDatetime = new Date(slot.datetime);
                        const overlappingSessions = getOverlappingSessions(slotDatetime);
                        const isInSession = overlappingSessions.length > 0;
                        const sessionNameStr = formatTableSessionParts(overlappingSessions, slotDatetime, timezone);
                        const isFirstOfDay = isFirstSlotOfDay(idx + 1);
                        return (
                          <th
                            key={idx}
                            style={{
                              ...getTimeHeaderCellStyle(isFirstOfDay),
                              padding: '4px 8px',
                              borderBottom: `1px solid ${isDark ? '#434343' : '#e8e8e8'}`,
                              top: 0,
                            }}
                          >
                            {isInSession ? (
                              <span
                                style={{
                                  fontSize: '11px',
                                  fontWeight: 600,
                                  color: '#1890ff',
                                  display: 'block',
                                  minHeight: 18,
                                }}
                              >
                                {sessionNameStr}
                              </span>
                            ) : (
                              <span style={{ display: 'block', minHeight: 18 }} />
                            )}
                          </th>
                        );
                      })}
                    </tr>
                  )}
                  {/* Date/Time row */}
                  <tr>
                    <th
                      style={{
                        position: 'sticky',
                        top: sessions.length > 0 ? 36 : 0,
                        left: 0,
                        backgroundColor: isDark ? '#1f1f1f' : '#ffffff',
                        padding: '8px',
                        borderBottom: `2px solid ${isDark ? '#434343' : '#d9d9d9'}`,
                        textAlign: 'left',
                        fontWeight: 600,
                        minWidth: '200px',
                        width: '200px',
                        zIndex: 3,
                      }}
                    >
                      Parameter
                    </th>
                    {tableData.timeSlots.map((slot, idx) => {
                      const slotDatetime = new Date(slot.datetime);
                      const overlappingSessions = getOverlappingSessions(slotDatetime);
                      const isInSession = overlappingSessions.length > 0;
                      const sessionTooltip = isInSession
                        ? formatTableSessionParts(overlappingSessions, slotDatetime, timezone)
                        : undefined;
                      const isFirstOfDay = isFirstSlotOfDay(idx + 1);

                      const timeContent = (
                        <div
                          style={{
                            fontSize: '13px',
                            display: 'inline-block',
                            padding: isInSession ? '2px 10px' : undefined,
                            borderRadius: isInSession ? 12 : undefined,
                            border: isInSession ? '2px solid #1890ff' : undefined,
                            backgroundColor: isInSession ? (isDark ? 'rgba(24, 144, 255, 0.15)' : 'rgba(24, 144, 255, 0.1)') : undefined,
                            color: isInSession ? '#1890ff' : undefined,
                          }}
                        >
                          {slot.timeStr}
                        </div>
                      );

                      return (
                        <th
                          key={idx}
                          style={{
                            ...getTimeHeaderCellStyle(isFirstOfDay),
                            top: sessions.length > 0 ? 36 : 0,
                          }}
                        >
                          <div style={{ fontSize: '11px', marginBottom: 4 }}>{slot.dateStr}</div>
                          {isInSession ? (
                            <Tooltip title={sessionTooltip}>
                              {timeContent}
                            </Tooltip>
                          ) : (
                            timeContent
                          )}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {tableDisplayFields.map(field => renderFieldRow(field))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Cards View - Hourly Forecast by Day - Horizontal Scrollable */}
      {viewMode === 'cards' && (
        <div
          ref={cardsContainerRef}
          style={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            overflow: 'auto',
            gap: 0,
            paddingBottom: 0,
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'thin',
          }}
        >
          {fieldsLoading ? (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 200 }}>
              <Spin size="large" tip="Loading field configuration..." />
            </div>
          ) : (
            forecastsByDay
              .filter(dayData => !filterBySession || dayHasSessions(dayData.dateKey))
              .map(dayData => renderDayCard(dayData))
          )}
        </div>
      )}

      {/* Chart View */}
      {viewMode === 'chart' && (
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <ForecastChartView
            forecasts={forecasts}
            selectedDate={selectedDate}
            forecastType="hourly"
            availableFields={tableDisplayFields}
            filterBySession={filterBySession}
            hoursBeforeSession={hoursBeforeSession}
            sessions={sessions}
          />
        </div>
      )}

      {/* Field Reorder Modal */}
      <FieldReorderModal
        open={isReorderModalOpen}
        fields={tableDisplayFields}
        onSave={(orderedFields) => {
          setSavingOrder(true);
          try {
            const dbNames = orderedFields.map(f => f.db_name);
            saveCustomFieldOrder(dbNames);
            setOrderVersion(v => v + 1); // Trigger re-render
            setIsReorderModalOpen(false);
          } catch (error) {
            console.error('Error saving field order:', error);
          } finally {
            setSavingOrder(false);
          }
        }}
        onCancel={() => setIsReorderModalOpen(false)}
        onReset={() => {
          localStorage.removeItem('hourlyTableFieldOrder');
          setOrderVersion(v => v + 1); // Trigger re-render
          setIsReorderModalOpen(false);
        }}
        saving={savingOrder}
        title="Reorder Hourly Forecast Fields"
      />

      {/* Day Details Modal */}
      {openModalDayKey && (() => {
        const dayData = forecastsByDay.find(d => d.dateKey === openModalDayKey);
        if (!dayData) return null;

        const { previousNight, weatherSummary, valueDate } = dayData;
        const dateStr = formatDateKeyForHeader(openModalDayKey);

        return (
          <Modal
            title={`${dateStr} - Full Details`}
            open={!!openModalDayKey}
            onCancel={() => setOpenModalDayKey(null)}
            footer={[
              <Button key="close" onClick={() => setOpenModalDayKey(null)}>
                Close
              </Button>
            ]}
            width={800}
            styles={{
              body: {
                maxHeight: '70vh',
                overflowY: 'auto',
                padding: '24px',
              },
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
              {previousNight && (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                    <Title level={4} style={{ margin: 0, fontSize: '16px' }}>🌙 Previous Night</Title>
                    {isEditable && onSavePreviousNight && (
                      <EditableTextModal
                        title="Previous Night"
                        initialValue={previousNight}
                        onSave={(newText) => onSavePreviousNight(valueDate, newText, dayData.dateKey)}
                        buttonSize="small"
                      />
                    )}
                  </div>
                  <div
                    style={{
                      marginBottom: 0,
                      wordWrap: 'break-word',
                      padding: '12px',
                      backgroundColor: isDark ? '#141414' : '#f5f5f5',
                      borderRadius: 4,
                    }}
                  >
                    <ForecastMarkdown source={previousNight} isDark={isDark} />
                  </div>
                </div>
              )}

              {weatherSummary && (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                    <Title level={4} style={{ margin: 0, fontSize: '16px' }}>☁️ Weather Conditions</Title>
                    {isEditable && onSaveWeatherSummary && (
                      <EditableTextModal
                        title="Weather Conditions"
                        initialValue={weatherSummary}
                        onSave={(newText) => onSaveWeatherSummary(valueDate, newText, dayData.dateKey)}
                        buttonSize="small"
                      />
                    )}
                  </div>
                  <div
                    style={{
                      marginBottom: 0,
                      wordWrap: 'break-word',
                      padding: '12px',
                      backgroundColor: isDark ? '#141414' : '#f5f5f5',
                      borderRadius: 4,
                    }}
                  >
                    <ForecastMarkdown source={weatherSummary} isDark={isDark} />
                  </div>
                </div>
              )}

              {!previousNight && !weatherSummary && (
                <Text type="secondary">No additional details available for this day.</Text>
              )}
            </div>
          </Modal>
        );
      })()}
    </div>
  );
};
